// submitGuards.ts — jaring pre-submit checkout (return pesan error atau null)
import { isKnownDuitkuMethod } from '../../../../constants/duitkuFormMethods';
import { isCodBlockedByShipping, resolveCodShippingLimit } from '../../../../constants/codRules';
import { fetchProductCourierRules, isBlockedCourierCanon } from '../../../../services/productCourierRules';
import { assertRatePerfLive } from '../../../../services/ratePerfAssert';
import { fetchGatewayStatus } from '../../../../services/paymentGatewayStatus';

export interface SubmitGuardCtx {
  form: any; addressData: any;
  selectedPaymentKey: unknown; selectedDuitkuMethod?: string | null;
  selectedEverproRate: any; selectedBiteshipRate?: any;
  shippingCost: number; isCODVal: boolean;
  minMid: number;
  // Tarif wajib dipilih (insiden #5c274e7d: submit Rp0 + kurir kosong).
  // rateRequired = popup tarif tampil; rateHold = popup status hold (tak ada
  // kurir layak / scoreless → ongkir menyusul CS, tetap boleh submit).
  // Agregator = Everpro ATAU Mengantar ATAU Lincah.
  brandEverpro?: { configured?: boolean };
  brandMengantar?: { configured?: boolean };
  brandLincah?: { configured?: boolean };
  rateHold?: boolean;
  // Rute-lemah (aturan user 2026-10-17): tak ada kurir ≥80% → COD dikunci.
  lowPerfNoCod?: boolean;
  // Dropdown non-Everpro: metode radio (dipakai guard di bawah).
  selectedShippingKey?: unknown;
  // Streaming kurir (speed 2026-10-22): false = masih ada provider yg jalan.
  // Lanjut/submit dikunci sampai true (aturan user) — tanpa ini order bisa
  // jalan dengan daftar parsial (kurir termurah belum tampil).
  ratesSettled?: boolean;
}

// Urutan: tarif wajib (agregator) → metode wajib (dropdown non-agregator) → gateway Duitku → gate performa 2-gate → cek blokir fresh → COD×ongkir → COD×rute-lemah.
// Return string error (hook yang set submission) atau null kalau lolos semua.
export async function runSubmitGuards(ctx: SubmitGuardCtx): Promise<string | null> {
  const { form, addressData, selectedPaymentKey, selectedDuitkuMethod,
    selectedEverproRate, shippingCost, isCODVal, minMid } = ctx;

  // Guard Duitku: metode harus valid + gateway brand masih siap. Kalau admin
  // mencabut kredensial setelah halaman dibuka, tolak dengan pesan ramah.
  if (selectedPaymentKey === 'duitku') {
    if (!isKnownDuitkuMethod(selectedDuitkuMethod)) {
      return 'Pilih dulu metode pembayaran otomatis (QRIS / VA / E-Wallet).';
    }
    try {
      const gw = await fetchGatewayStatus(form?.brandId);
      if (!gw.duitku) {
        return 'Pembayaran otomatis sedang tidak tersedia, silakan pilih Transfer Bank atau COD.';
      }
    } catch { /* RPC gagal = lanjutkan, backend yang menolak kalau kredensial hilang */ }
  }

  // Tarif wajib dipilih (insiden #5c274e7d 2026-10-05): mode tarif tampil tapi
  // tanpa rate terpilih + tanpa hold = customer belum pilih → tolak.
  // Hold (tak ada kurir layak / scoreless) = ongkir menyusul CS → lolos.
  {
    const mode = (form as any)?.addressInputMode;
    const addrText = String((addressData as any)?.detailAddress || (addressData as any)?.fullAddress || '');
    const postalMatch = addrText.match(/\b\d{5}\b/);
    const destPostal = (addressData as any)?.postalCode || (postalMatch ? postalMatch[0] : '');
    const hasAggregator = !!ctx.brandEverpro?.configured || !!ctx.brandMengantar?.configured || !!ctx.brandLincah?.configured;
    const rateRequired = mode !== 'none' && mode !== 'simple'
      && hasAggregator && !!destPostal;
    const hasRate = !!(selectedEverproRate || (ctx as any).selectedBiteshipRate);
    if (rateRequired && !hasRate && !ctx.rateHold) {
      return 'Pilih kurir pengiriman dulu.';
    }
    // Streaming kurir (speed 2026-10-22): daftar boleh tampil bertahap, tapi
    // submit DIKUNCI sampai semua provider selesai (aturan user) — tanpa ini
    // order bisa jalan padahal kurir termurah belum tampil. undefined = jalur
    // lama/tak tahu (jangan blokir).
    if (rateRequired && ctx.ratesSettled === false && !ctx.rateHold) {
      return 'Tunggu semua kurir selesai dimuat dulu.';
    }
    // Dropdown TANPA agregator (aturan user 2026-10-17: wajib ada ongkir): metode
    // pengiriman wajib dipilih — jangan lolos diam-diam Rp0 karena admin belum
    // konfigurasi metode visible sama sekali. 'regular'/gratis/subsidi-0 tetap
    // lolos (tarif real diisi server via sweep / promo eksplisit).
    if (mode === 'dropdown' && !hasAggregator && !ctx.selectedShippingKey) {
      return 'Pilih metode pengiriman dulu.';
    }
  }

  // Gate performa 2-gate = popup kebenaran (2026-10-03, insiden #6f57c474):
  // tarif terukur < ambang tak boleh di-checkout (non-Jawa). Skor null
  // (pilih sebelum skor tiba) → cek ulang live sekali di helper.
  {
    const err = await assertRatePerfLive(selectedEverproRate, {
      postal: (addressData as any)?.postalCode || '',
      city: (addressData as any)?.city || '',
    }, minMid, {
      singleCourier: !!(selectedEverproRate as any)?.singleCourier,
      paymentMethod: typeof selectedPaymentKey === 'string' ? selectedPaymentKey : '',
    });
    if (err) return err;
  }

  // Cek blokir fresh (tutup race "pilih sebelum aturan tiba", insiden SiCepat
  // #88dcc01c): aturan dibaca ulang saat submit, bukan mengandalkan daftar tampil.
  {
    const r = selectedEverproRate as any;
    if (r) {
      const rules = await fetchProductCourierRules((form as any)?.productId).catch(() => null);
      if (isBlockedCourierCanon(rules, r?.logistic_code || r?.rate_code, r?.logistic_name)) {
        return 'Kurir tersebut tidak mendukung produk ini. Silakan pilih kurir lain.';
      }
    }
  }

  // Mode dropdown+wizard: COD + ongkir ASLI > maks = ditolak deterministik (tanpa retry).
  // Normalnya opsi COD sudah hilang + auto-switch di effectsSync, ini jaring pengaman
  // kalau state terkunci sebelum tarif kurir ke-refresh. Cek pakai MAX(final, asli)
  // supaya subsidi tidak membuka COD kembali (koreksi 2026-11-04).
  const codLimit = resolveCodShippingLimit(form?.paymentSettings?.cod);
  const origCost = (selectedEverproRate as any)?.price ?? shippingCost;
  if (isCODVal && codLimit.enabled && isCodBlockedByShipping(shippingCost, form?.addressInputMode, codLimit.max, origCost)) {
    return `COD tidak tersedia untuk ongkir di atas Rp${codLimit.max.toLocaleString('id-ID')}, silakan pilih Transfer Bank atau QRIS.`;
  }

  // Rute-lemah (aturan user 2026-10-17): tak ada kurir ≥80% → COD ditolak
  // deterministik. Normalnya opsi COD sudah hilang + auto-switch di effectsSync,
  // ini jaring pengaman kalau state terkunci sebelum tarif kurir ke-refresh.
  if (isCODVal && ctx.lowPerfNoCod) {
    return 'COD tidak tersedia untuk rute ini (kurir di bawah standar, risiko retur tinggi) — silakan pilih Transfer Bank atau QRIS.';
  }

  return null;
}
